/**
 * Editorial treat photos — real bakery stills, no emoji frames.
 */

import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { menuPathForMenuItemId } from '../../utils/menuDeepLink';
import type { PreviewCampaign, PreviewProduct } from './usePreviewHomeData';

export function PreviewTreats({ products }: { products: PreviewProduct[] }) {
  if (products.length === 0) return null;

  return (
    <section className="section-padding bg-white">
      <div className="container-custom">
        <div className="mb-10 max-w-2xl">
          <p className="text-sm font-semibold uppercase tracking-[0.2em] text-primary-700">From the case</p>
          <h2 className="mt-2 font-display text-3xl font-bold text-gray-900 md:text-4xl">
            Cakes, cookies, and daily treats
          </h2>
          <p className="mt-3 text-gray-600">
            Featured items from the live menu. Click any photo to order or see details.
          </p>
        </div>

        <div className="grid grid-cols-2 gap-4 md:grid-cols-3">
          {products.map((product, index) => (
            <Link
              key={product.id || product.name}
              to={menuPathForMenuItemId(product.id)}
              className="group block overflow-hidden rounded-2xl bg-bakery-cream shadow-sm"
            >
              <motion.div
                initial={{ opacity: 0, y: 12 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
              >
                <div className="aspect-[4/5] overflow-hidden">
                  <img
                    src={product.src}
                    alt={product.alt}
                    className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105"
                  />
                </div>
                <div className="p-4">
                  <h3 className="font-display text-lg font-semibold text-gray-900">{product.name}</h3>
                  {product.price != null && (
                    <p className="mt-1 text-sm font-medium text-primary-700">${product.price.toFixed(2)}</p>
                  )}
                </div>
              </motion.div>
            </Link>
          ))}
        </div>

        <div className="mt-10">
          <Link to="/menu" className="btn-primary">
            See the full menu
          </Link>
        </div>
      </div>
    </section>
  );
}

export function PreviewCampaigns({ campaigns }: { campaigns: PreviewCampaign[] }) {
  if (campaigns.length === 0) return null;

  return (
    <section className="border-y border-rose-100 bg-rose-50/70 py-14">
      <div className="container-custom">
        <h2 className="font-display text-3xl font-bold text-gray-900">Live fundraisers</h2>
        <p className="mt-2 text-gray-600">Support a local group. Same checkout Tammy already uses.</p>
        <div className="mt-8 grid gap-4 md:grid-cols-3">
          {campaigns.map((campaign) => (
            <Link
              key={campaign.id}
              to={`/campaign/${campaign.slug}`}
              className="overflow-hidden rounded-2xl bg-white shadow-sm"
            >
              <img
                src={
                  campaign.hero_image_url ||
                  campaign.menu_item_image_url ||
                  '/assets/images/products/product-image-2.webp'
                }
                alt={campaign.title}
                className="h-40 w-full object-cover"
              />
              <div className="p-4">
                <h3 className="font-display font-semibold text-gray-900">{campaign.title}</h3>
                {campaign.organizer_name && (
                  <p className="mt-1 text-xs text-gray-500">For {campaign.organizer_name}</p>
                )}
              </div>
            </Link>
          ))}
        </div>
      </div>
    </section>
  );
}
